import React, { useEffect } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-angle-dashboard-components'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import './PreviousPlanFormModal.css'
import {
  HSA,
  IPreviousPlanForm,
  TRADITIONAL,
  previousPlanSchema
} from './previousPlanSchema'
import { InvalidFeedback } from '../../style'

interface PreviousPlanFormModalProps {
  open: boolean
  onClose: () => void
  onCreate: (previousPlan: IPreviousPlanForm) => void
  formValues?: IPreviousPlanForm
}

const PreviousPlanFormModal = ({
  open,
  onClose,
  onCreate,
  formValues
}: PreviousPlanFormModalProps) => {
  const { register, handleSubmit, reset, formState } =
    useForm<IPreviousPlanForm>({
      resolver: zodResolver(previousPlanSchema)
    })

  const errors = formState.errors

  useEffect(() => {
    reset(formValues)
  }, [formValues, reset])

  const createPreviousPlan = (previousPlan: IPreviousPlanForm) => {
    onCreate(previousPlan)
  }

  return (
    <Modal
      show={open}
      centered
      onHide={onClose}
      dialogClassName="previousPlanFormModalDialog"
      className="previousPlanFormModal"
      style={{
        zIndex: 2000
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add previous plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Plan Name</Form.Label>
              <Form.Control
                {...register('planName')}
                isInvalid={!!errors.planName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.planName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Check>
                <Form.Check.Input
                  type="radio"
                  value={TRADITIONAL}
                  {...register('type')}
                  isInvalid={!!errors.type}
                />
                <Form.Check.Label>Traditional</Form.Check.Label>
              </Form.Check>
              <Form.Check>
                <Form.Check.Input
                  type="radio"
                  value={HSA}
                  {...register('type')}
                  isInvalid={!!errors.type}
                />
                <Form.Check.Label>HDHP (HSA Eligible)</Form.Check.Label>
              </Form.Check>
              {!!errors.type && (
                <InvalidFeedback>{errors.type.message}</InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Individual In-network Deductible ($)</Form.Label>
              <Form.Control
                {...register('indInnDeductible')}
                isInvalid={!!errors.indInnDeductible}
              />
              <Form.Control.Feedback type="invalid">
                {errors.indInnDeductible?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                Individual In-network Out-of-pocket Maximum ($)
              </Form.Label>
              <Form.Control
                {...register('indInnOOP')}
                isInvalid={!!errors.indInnOOP}
              />
              <Form.Control.Feedback type="invalid">
                {errors.indInnOOP?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Member Coinsurance (%)</Form.Label>
              <Form.Control
                {...register('memberCoinsurance')}
                isInvalid={!!errors.memberCoinsurance}
              />
              <Form.Control.Feedback type="invalid">
                {errors.memberCoinsurance?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col />
        </Row>
        <Row>
          <Col>
            <p>Enrollment By Tier (Optional)</p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Employee-Only</Form.Label>
              <Form.Control
                {...register('enrollmentCount.employeeOnly')}
                isInvalid={!!errors.enrollmentCount?.employeeOnly}
              />
              <Form.Control.Feedback type="invalid">
                {errors.enrollmentCount?.employeeOnly?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Spouse</Form.Label>
              <Form.Control
                {...register('enrollmentCount.employeePlusSpouse')}
                isInvalid={!!errors.enrollmentCount?.employeePlusSpouse}
              />
              <Form.Control.Feedback type="invalid">
                {errors.enrollmentCount?.employeePlusSpouse?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Children</Form.Label>
              <Form.Control
                {...register('enrollmentCount.employeePlusChildren')}
                isInvalid={!!errors.enrollmentCount?.employeePlusChildren}
              />
              <Form.Control.Feedback type="invalid">
                {errors.enrollmentCount?.employeePlusChildren?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Family</Form.Label>
              <Form.Control
                {...register('enrollmentCount.employeePlusFamily')}
                isInvalid={!!errors.enrollmentCount?.employeePlusFamily}
              />
              <Form.Control.Feedback type="invalid">
                {errors.enrollmentCount?.employeePlusFamily?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <p>Current Rates By Tier (Optional)</p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Employee-Only</Form.Label>
              <Form.Control
                {...register('rates.employeeOnly')}
                isInvalid={!!errors.rates?.employeeOnly}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rates?.employeeOnly?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Spouse</Form.Label>
              <Form.Control
                {...register('rates.employeePlusSpouse')}
                isInvalid={!!errors.rates?.employeePlusSpouse}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rates?.employeePlusSpouse?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Children</Form.Label>
              <Form.Control
                {...register('rates.employeePlusChildren')}
                isInvalid={!!errors.rates?.employeePlusChildren}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rates?.employeePlusChildren?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Family</Form.Label>
              <Form.Control
                {...register('rates.employeePlusFamily')}
                isInvalid={!!errors.rates?.employeePlusFamily}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rates?.employeePlusFamily?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <p>Renewal Rates By Tier (Optional)</p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Employee-Only</Form.Label>
              <Form.Control
                {...register('renewalRates.employeeOnly')}
                isInvalid={!!errors.renewalRates?.employeeOnly}
              />
              <Form.Control.Feedback type="invalid">
                {errors.renewalRates?.employeeOnly?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Spouse</Form.Label>
              <Form.Control
                {...register('renewalRates.employeePlusSpouse')}
                isInvalid={!!errors.renewalRates?.employeePlusSpouse}
              />
              <Form.Control.Feedback type="invalid">
                {errors.renewalRates?.employeePlusSpouse?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Children</Form.Label>
              <Form.Control
                {...register('renewalRates.employeePlusChildren')}
                isInvalid={!!errors.renewalRates?.employeePlusChildren}
              />
              <Form.Control.Feedback type="invalid">
                {errors.renewalRates?.employeePlusChildren?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Employee + Family</Form.Label>
              <Form.Control
                {...register('renewalRates.employeePlusFamily')}
                isInvalid={!!errors.renewalRates?.employeePlusFamily}
              />
              <Form.Control.Feedback type="invalid">
                {errors.renewalRates?.employeePlusFamily?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-primary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(createPreviousPlan)}>
          {formValues ? 'Edit' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PreviousPlanFormModal
